// State-layer types for Viewer 2.0 (framework-agnostic, no React/MapLibre).
//
// Models the viewer's configuration with a draft-vs-applied split: the config
// bar edits `draft`, and the explicit Update action promotes draft → applied
// (which is what actually drives the map). See docs/viewer-v2/PLAN.md §3.

/** Which map a config belongs to. Single view uses only "left". */
export type MapSide = "left" | "right";

/** How the basemap is chosen for a side. */
export type BasemapSelectionType = "select" | "manual";

/**
 * One side's editable map configuration. This is the draft the config bar
 * mutates and, once applied, what the engine renders.
 */
export interface SideConfig {
    /** "select" (catalog dropdowns) or "manual" (paste a stylesheet URL). */
    selectionType: BasemapSelectionType;
    /** Catalog source/environment, e.g. "medas_prod". */
    environment: string;
    /** Selected stylesheet id (select mode). */
    stylesheetId: string;
    /** Selected stylesheet version (select mode). */
    stylesheetVersion: string;
    /** Manual stylesheet URL (manual mode). */
    manualStylesheetUrl: string;
    /** Optional tileset URL override; blank = use the stylesheet's own tiles. */
    tilesetUrl: string;
    /** Free-text label shown on this side in compare/slider views. */
    label: string;
}

/** Instant client-side toggles (no refetch); apply immediately to the map. */
export interface ViewerToggles {
    /** X-ray debug rendering. */
    xray: boolean;
    /** Draw vector tile boundaries. */
    tileBoundaries: boolean;
    /** Feature inspection mode: off, popup, or side panel. */
    inspect: InspectMode;
}

/** Inspect interaction mode. Single on-map control cycles via a flyout. */
export type InspectMode = "off" | "popup" | "panel";

/** Camera state mirrored into the store (and serialized to the URL). */
export interface ViewerCamera {
    lng: number;
    lat: number;
    zoom: number;
    pitch: number;
    bearing: number;
}

/** Default camera — Seattle, matching the 1.0 viewer's initial location. */
export const DEFAULT_CAMERA: ViewerCamera = {
    lng: -122.3394,
    lat: 47.6077,
    zoom: 8.57,
    pitch: 0,
    bearing: 0,
};

/** A sensible empty side config (select mode, default catalog). */
export function defaultSideConfig(label: string): SideConfig {
    return {
        selectionType: "select",
        environment: "medas_prod",
        stylesheetId: "amazon-delivery-us",
        // Default stylesheet version bumped to 3.9 for Viewer 2.0 (was 3.0 in 1.0).
        stylesheetVersion: "3.9",
        manualStylesheetUrl: "",
        tilesetUrl: "",
        label,
    };
}

/** Default instant toggles — all off. */
export const DEFAULT_TOGGLES: ViewerToggles = {
    xray: false,
    tileBoundaries: false,
    inspect: "off",
};
